import {
  GetSubscriptionInfo200Response,
  GetSubscriptions200Response,
  GetSubscriptionsPreCheckout200Response,
  UpdateSubscriptions200Response,
  UpdateSubscriptionsRequest,
} from "@coeff/api";

import { ACTIONS, BILLING_ROUTES } from "../constants";

import { getClientFlags } from "./app";

import { AppThunkAction } from ".";

export type BillingActionError = { is_success: false; error_msg: string; status: number };

export type BillingActionResult<T = UpdateSubscriptions200Response> = T | BillingActionError;

const TOKEN_REFRESH_TIMEOUT_MS = 15_000;

const SESSION_EXPIRED_MSG =
  "Your session has expired. Please re-open the billing page to continue.";

const getNewTokenForBilling = (): Promise<{ oauthToken: string; openIdToken: string }> => {
  const opener = window.opener;
  if (!opener) {
    return Promise.reject(new Error("TOKEN_REFRESH_UNAVAILABLE"));
  }

  const expectedOrigin = window.location.origin;
  opener.postMessage(JSON.stringify({ type: "tokens:get" }), expectedOrigin);

  return new Promise((resolve, reject) => {
    const handler = (evt: MessageEvent) => {
      if (evt.source !== opener || evt.origin !== expectedOrigin) {
        return;
      }
      if (evt.data) {
        try {
          const response = JSON.parse(evt.data);
          if (response.type === "tokens:new") {
            clearTimeout(timeout);
            window.removeEventListener("message", handler);
            resolve(response);
          }
        } catch {
          /* ignore non-JSON messages */
        }
      }
    };

    const timeout = setTimeout(() => {
      window.removeEventListener("message", handler);
      reject(new Error("TOKEN_REFRESH_UNAVAILABLE"));
    }, TOKEN_REFRESH_TIMEOUT_MS);

    window.addEventListener("message", handler, false);
  });
};

const fetchURLWithNewTokens = async () => {
  const tokens = await getNewTokenForBilling();

  const params = new URLSearchParams(window.location.search);
  params.set("GOOGLE-OAUTH-TOKEN", tokens.oauthToken);
  params.set("GOOGLE-OPENID-TOKEN", tokens.openIdToken);
  return { pathname: window.location.pathname, search: params.toString() };
};

export const getPreCheckoutData =
  (subscriptionId?: string): AppThunkAction<Promise<GetSubscriptionsPreCheckout200Response>> =>
  async (dispatch, __, { api }) => {
    dispatch({ type: ACTIONS.SET_BILLING_LOADING });

    try {
      const { headers, status, ...response } = await api.request({
        method: "GET",
        url:
          subscriptionId && subscriptionId !== "undefined"
            ? `${BILLING_ROUTES.billingPreCheckout}?subscription_id=${subscriptionId}`
            : BILLING_ROUTES.billingPreCheckout,
      });

      // sort pro users to top
      response.users_from_domain.sort((a, b) => (a.plan_type ? -1 : 1));
      dispatch({ type: ACTIONS.SET_BILLING_METADATA, metadata: response });
      dispatch({
        type: ACTIONS.SET_USER_DETAILS,
        userEmail: headers["x-coeff-user-email"],
        userId: headers["x-coeff-user-id"],
      });

      return response;
    } catch (error) {
      return error;
    }
  };

export const getUserSubscription =
  (subscriptionId?: string): AppThunkAction<Promise<GetSubscriptionInfo200Response>> =>
  async (dispatch, __, { api }) => {
    try {
      const { headers, status, ...response } = await api.request<GetSubscriptionInfo200Response>({
        method: "GET",
        url:
          subscriptionId && subscriptionId !== "undefined"
            ? `${BILLING_ROUTES.billingSubscriptionInfo}?subscription_id=${subscriptionId}`
            : BILLING_ROUTES.billingSubscriptionInfo,
      });

      dispatch({ type: ACTIONS.SET_BILLING_SUBSCRIPTION, userSubscription: response });

      return response;
    } catch (error: any) {
      if (error.status === 404) {
        dispatch({ type: ACTIONS.SET_BILLING_SUBSCRIPTION, userSubscription: null });
      }

      return error;
    }
  };

export const getAllSubscriptions =
  (): AppThunkAction<Promise<GetSubscriptions200Response>> =>
  async (dispatch, __, { api }) => {
    try {
      const { headers, status, ...response } = await api.request<GetSubscriptions200Response>({
        method: "GET",
        url: BILLING_ROUTES.bilingAllSubscriptions,
      });

      dispatch({ type: ACTIONS.SET_ALL_SUBSCRIPTIONS, subscriptions: response });
      return response;
    } catch (error: any) {
      if (error.status === 404) {
        dispatch({ type: ACTIONS.SET_ALL_SUBSCRIPTIONS, subscriptions: [] });
      }

      return error;
    }
  };

const checkoutInner =
  (body: any, isRetry: boolean): AppThunkAction<Promise<BillingActionResult>> =>
  async (dispatch, __, { api, history }) => {
    try {
      const { headers, status, ...response } = await api.request({
        method: "POST",
        url: BILLING_ROUTES.billingCheckout,
        body,
      });

      dispatch(getClientFlags());
      return response;
    } catch (error: any) {
      if (error.status === 401 && !isRetry) {
        try {
          const newURLState = await fetchURLWithNewTokens();
          history.push(newURLState);
          return dispatch(checkoutInner(body, true));
        } catch {
          return { is_success: false, error_msg: SESSION_EXPIRED_MSG, status: 401 };
        }
      }
      if (error.status === 401) {
        return { is_success: false, error_msg: SESSION_EXPIRED_MSG, status: 401 };
      }
      return {
        is_success: false,
        error_msg: error?.error?.error_msg || error?.message,
        status: error?.status,
      };
    }
  };

export const checkout =
  (body: any): AppThunkAction<Promise<BillingActionResult>> =>
  dispatch =>
    dispatch(checkoutInner(body, false));

const updateSubscriptionInner =
  (
    body: UpdateSubscriptionsRequest,
    isRetry: boolean
  ): AppThunkAction<Promise<BillingActionResult>> =>
  async (dispatch, __, { api, history }) => {
    try {
      const { headers, status, ...response } = await api.request({
        method: "POST",
        url: BILLING_ROUTES.billingSubscriptionUpdate,
        body,
      });

      dispatch(getClientFlags());

      window.opener?.postMessage(
        JSON.stringify({ type: "subscription:update" }),
        window.location.origin
      );

      return response;
    } catch (error: any) {
      if (error.status === 401 && !isRetry) {
        try {
          const newURLState = await fetchURLWithNewTokens();
          history.push(newURLState);
          return dispatch(updateSubscriptionInner(body, true));
        } catch {
          return { is_success: false, error_msg: SESSION_EXPIRED_MSG, status: 401 };
        }
      }
      if (error.status === 401) {
        return { is_success: false, error_msg: SESSION_EXPIRED_MSG, status: 401 };
      }
      return {
        is_success: false,
        error_msg: error?.error?.error_msg || error?.message,
        status: error?.status,
      };
    }
  };

export const updateSubscription =
  (body: UpdateSubscriptionsRequest): AppThunkAction<Promise<BillingActionResult>> =>
  dispatch =>
    dispatch(updateSubscriptionInner(body, false));

export const getInvoices =
  (subscriptionId?: string): AppThunkAction<unknown> =>
  async (dispatch, __, { api }) => {
    try {
      const { headers, status, ...response } = await api.request({
        method: "GET",
        url:
          subscriptionId && subscriptionId !== "undefined"
            ? `${BILLING_ROUTES.billingInvoices}?subscription_id=${subscriptionId}`
            : BILLING_ROUTES.billingInvoices,
      });

      dispatch({ type: ACTIONS.SET_BILLING_INVOICES, invoices: response.invoices });
      dispatch({ type: ACTIONS.SET_UPGRADE_TOKEN, upgradeToken: response.upgrade_token });
      return response;
    } catch (error: any) {
      if (error.status === 404) {
        dispatch({ type: ACTIONS.SET_BILLING_INVOICES, invoices: [] });
      }
      return error;
    }
  };
